import { z } from 'zod'
import { type Expand } from '@/types/ui/expand'
import { ENGAGEMENT_TYPE, ENGAGEMENT_REQUEST_TYPE } from '@/lib/constants/engagement-codes'
import { zodValidationHelpers } from '@/lib/utils/zod-validation-helpers'

/**
 * Reusable validation logic for closed date fields.
 * Used in both the core form and the complete engagement dialog.
 */
export const validateClosedDateFields = (
  ctx: z.RefinementCtx,
  closedDate: Date | null,
  closedReason: string,
  startDate: Date | null,
  endDate: Date | null
) => {
  if (closedDate) {
    if (!closedReason) {
      zodValidationHelpers.requiredString(ctx, ['closedReason'])
    }
    
    if (startDate && closedDate < startDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Closed date must be on or after start date',
        path: ['closedDate'],
      })
    }
    else if (endDate && closedDate > endDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Closed date must be on or before end date',
        path: ['closedDate'],
      })
    }
  }
}

// Base schema without conditional validation
const baseCoreFormSchema = z.object({
  engagementName: z.string().min(1, 'Field is required'),
  engagementCode: z.string().min(1, 'Field is required'),
  engagementType: z.string().min(1, 'Field is required'),
  requestType: z.string(),
  selectedClient: z.number(),
  selectedContact: z.number(),
  startDate: z.date().nullable(),
  endDate: z.date().nullable(),
  closedDate: z.date().nullable(),
  closedReason: z.string(),
  selectedInternalCompany: z.number().min(1, 'Field is required'),
  selectedBranch: z.string().min(1, 'Field is required'),
  selectedSalesPerson: z.number(),
  selectedEngagementLead: z.number().min(1, 'Field is required'),
  selectedProjectLead: z.number().optional(),
  selectedClientExecutive: z.number().optional(),
  selectedOriginalEngagement: z.number(),
  agreementSignedDate: z.date().nullable(),
  isPreEngagement: z.boolean().default(false),
})

// Schema with conditional validation (optimized)
export const coreFormSchema = baseCoreFormSchema.superRefine((data, ctx) => {
  // Cache common values to avoid repeated property access
  const { engagementType, startDate, endDate, closedDate, closedReason } = data
  
  // Required date validations (always run)
  if (!startDate) {
    zodValidationHelpers.requiredDate(ctx, ['startDate'])
  }

  // End date is only required for non-internal engagements
  if (!endDate && engagementType !== ENGAGEMENT_TYPE.INTERNAL) {
    zodValidationHelpers.requiredDate(ctx, ['endDate'])
  }

  // Date range validations (only if both dates exist)
  if (startDate && endDate && endDate <= startDate) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'End date must be after start date',
      path: ['endDate'],
    })
  }

  // Closed date validations (only if closed date exists)
  validateClosedDateFields(ctx, closedDate, closedReason, startDate, endDate)

  // Client field validations (only required for non-internal engagements)
  if (engagementType !== ENGAGEMENT_TYPE.INTERNAL) {
    if (!data.selectedClient || data.selectedClient === 0) {
      zodValidationHelpers.requiredNumber(ctx, ['selectedClient'])
    }

    if (!data.selectedContact || data.selectedContact === 0) {
      zodValidationHelpers.requiredNumber(ctx, ['selectedContact'])
    }
  }

  // Advisory engagement validations (grouped together)
  if (engagementType === ENGAGEMENT_TYPE.ADVISORY) {
    if (!data.requestType) {
      zodValidationHelpers.requiredString(ctx, ['requestType'])
    }

    if (!data.selectedClientExecutive || data.selectedClientExecutive === 0) {
      zodValidationHelpers.requiredNumber(ctx, ['selectedClientExecutive'])
    }

    // Original engagement only required for Change engagements
    if (data.requestType === ENGAGEMENT_REQUEST_TYPE.CHANGE && 
        (!data.selectedOriginalEngagement || data.selectedOriginalEngagement === 0)) {
      zodValidationHelpers.requiredNumber(ctx, ['selectedOriginalEngagement'])
    }
  }
})

// Inferred TypeScript type
export type CoreFormData = Expand<z.infer<typeof coreFormSchema>>

/**
 * Schema factory for complete engagement dialog.
 * Validates closed date fields using the same business rules as the core form.
 */
export const createCompleteEngagementSchema = (startDate: Date | null, endDate: Date | null) => {
  return z.object({
    closedDate: z.date().nullable(),
    closedReason: z.string()
  }).superRefine((data, ctx) => {
    // Reuse the same validation logic as the core form
    validateClosedDateFields(ctx, data.closedDate, data.closedReason, startDate, endDate)
  })
}